import React from "react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faCommentDots,
  faExclamationCircle,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";

import Datalist, { ListColumn } from "../Datalist/Datalist";
import InternalCommentsModal from "../InternalComments/InternalCommentsModal";
import "./FollowUpSendingTable.scss";

interface PhoneReviewRow {
  id: number;
  supplierName: string;
  supplierOrgNr: string;
  customerName: string;
  customerOrgNr: string;
  contactPerson?: string;
  period: string;
  endPeriod: string;
  status: number;
  isHandled: boolean;
  created: string;
  updated: string;
  hasInternalComment?: boolean;
}

interface FollowUpPhoneReviewTableProps {
  isLoading: boolean;
  phoneReviewData: PhoneReviewRow[];
  refetchData: () => void;
}

const phoneReviewStatusMap: Record<
  number,
  { label: string; color: string; icon: any }
> = {
  0: { label: "Utkast", color: "#6c757d", icon: faExclamationCircle },
  1: { label: "Pågående", color: "#0d6efd", icon: faExclamationCircle },
  2: { label: "Svar mottaget", color: "#f59e0b", icon: faCheckCircle },
  3: { label: "Slutförd", color: "#07A235", icon: faCheckCircle },
};

const FollowUpPhoneReviewTable: React.FC<FollowUpPhoneReviewTableProps> = ({
  isLoading,
  phoneReviewData,
  refetchData,
}) => {
  const navigate = useNavigate();

  const [showCommentsModal, setShowCommentsModal] = React.useState(false);
  const [commentsTargetId, setCommentsTargetId] = React.useState<number | null>(null);

  const openComments = (reviewPhoneReviewId: number) => {
    setCommentsTargetId(reviewPhoneReviewId);
    setShowCommentsModal(true);
  };

  const columns: ListColumn<PhoneReviewRow>[] = [
    {
      key: "id",
      fieldName: "id",
      name: "Id",
      maxWidth: "10%",
      minWidth: 50,
      isActive: true,
      sorting: true,
      onRender: (item) => item.id.toString(),
      footer: (items: PhoneReviewRow[]) => `Totalt antal rader: ${items.length}`,
    },
    {
      key: "internalComments",
      fieldName: "internalComments",
      name: "",
      maxWidth: 50,
      minWidth: 50,
      isActive: true,
      sorting: false,
      onRender: (item: PhoneReviewRow) => (
        <span
          onClick={(ev) => {
            ev.stopPropagation();
            openComments(item.id);
          }}
          style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
          title="Interna kommentarer"
        >
          <FontAwesomeIcon
            icon={faCommentDots}
            style={{ color: item.hasInternalComment ? "#0d6efd" : "#adb5bd" }}
          />
        </span>
      ),
    },
    {
      key: "customer",
      fieldName: "customer",
      name: "Medlem",
      maxWidth: "25%",
      minWidth: 140,
      isActive: true,
      sorting: true,
      onRender: (item) => `${item.customerName ?? "Okänd kund"} - ${item.customerOrgNr}`,
    },
    {
      key: "supplier",
      fieldName: "supplier",
      name: "Leverantör",
      maxWidth: "20%",
      minWidth: 120,
      isActive: true,
      sorting: true,
      onRender: (item) => item.supplierName,
    },
    {
      key: "contactPerson",
      fieldName: "contactPerson",
      name: "Kontaktperson",
      maxWidth: "20%",
      minWidth: 120,
      isActive: true,
      sorting: true,
      onRender: (item) => item.contactPerson || "—",
    },
    {
      key: "period",
      fieldName: "period",
      name: "Period",
      maxWidth: "16%",
      minWidth: 110,
      isActive: true,
      sorting: true,
      onRender: (item) =>
        item.period !== item.endPeriod ? `${item.period} - ${item.endPeriod}` : item.period,
    },
    {
      key: "created",
      fieldName: "created",
      name: "Datum",
      maxWidth: "14%",
      minWidth: 100,
      isActive: true,
      sorting: true,
      onRender: (item) => {
        const parsedDate = new Date(item.created);
        return isNaN(parsedDate.getTime()) ? "Invalid Date" : format(parsedDate, "yyyy-MM-dd");
      },
    },
    {
      key: "updated",
      fieldName: "updated",
      name: "Uppdaterad",
      maxWidth: "14%",
      minWidth: 100,
      isActive: true,
      sorting: true,
      onRender: (item) => {
        const parsedDate = new Date(item.updated);
        return isNaN(parsedDate.getTime()) ? "Invalid Date" : format(parsedDate, "yyyy-MM-dd");
      },
    },
    {
      key: "status",
      fieldName: "status",
      name: "Status",
      maxWidth: "18%",
      minWidth: 110,
      isActive: true,
      sorting: true,
      onRender: (item) => {
        const statusInfo = phoneReviewStatusMap[item.status] || {
          label: "Okänd",
          color: "#000",
          icon: faExclamationCircle,
        };

        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <FontAwesomeIcon
              icon={statusInfo.icon}
              style={{ marginRight: 8, color: statusInfo.color }}
            />
            <span>{statusInfo.label}</span>
          </div>
        );
      },
    },
    {
      key: "isHandled",
      fieldName: "isHandled",
      name: "Slutförd",
      maxWidth: "12%",
      minWidth: 80,
      isActive: true,
      sorting: true,
      onRender: (item) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <FontAwesomeIcon
            icon={item.isHandled ? faCheckCircle : faTimesCircle}
            style={{ marginRight: 8, color: item.isHandled ? "#07A235" : "#DC143C" }}
          />
          <span>{item.isHandled ? "Ja" : "Nej"}</span>
        </div>
      ),
    },
  ];

  return (
    <div className="follow-up-sending-table-wrapper">
      <div style={{ maxHeight: "600px", overflowY: "auto" }}>
        <Datalist
          items={phoneReviewData}
          isLoading={isLoading}
          columns={columns}
          onClick={(item) => navigate(`/followup/phone-review/${item.id}`)}
          setColumns={() => {}}
          initialSortKey="updated"
          descendingSort={false}
          noItemsText="Inga telefongranskningar att visa"
          uniqueKey="id"
        />
      </div>

      {commentsTargetId !== null && (
        <InternalCommentsModal
          show={showCommentsModal}
          onClose={() => setShowCommentsModal(false)}
          targetType="review-phone-review"
          targetId={commentsTargetId}
          onCreated={refetchData}
        />
      )}
    </div>
  );
};

export default FollowUpPhoneReviewTable;